import { Input, RangeInput } from '@/components/form';
import _isEqualWith from 'lodash/isEqualWith';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { styled } from 'stitches.config';
import * as $segment from '@/utils/segment';
import { SimulatorActionBlock } from '../simulators/SimulatorActionBlock';
import { Results } from './Results';
import { FieldEnum, FormValues } from './types';
import { getSafeDefaultValue, updateSimulatorQueryParams } from '@/utils/simulators';

const SIMULATOR_NAME = 'wealth';

const initialState = {
  patrimony: 100000,
  annualInvestment: 6000,
  allowance: 50,
  nbYears: 20,
  stockYield: 8,
  yieldOther: 5,
  investAllowance: 50,
  stockTax: 17.2,
  tax: 30,
  withdrawalRate: 4,
  inflationRate: 3,
};

export const Simulator = () => {
  const { formatMessage } = useIntl();
  const { query, isReady } = useRouter();
  const initializedRef = React.useRef(false);
  const paramsInitializedRef = React.useRef(false);
  const resultsTrackedRef = React.useRef(false);
  const [formValues, setFormValues] = React.useState<FormValues>(initialState);

  React.useEffect(() => {
    $segment.event('simulator_started', { simulator: SIMULATOR_NAME });
  }, []);

  React.useEffect(() => {
    if (isReady && !resultsTrackedRef.current) {
      resultsTrackedRef.current = true;
      $segment.event('simulator_results_shown', { simulator: SIMULATOR_NAME });
    }
  }, [isReady]);

  React.useEffect(() => {
    if (isReady && !initializedRef?.current) {
      setFormValues({
        [FieldEnum.patrimony]: getSafeDefaultValue({
          value: query[FieldEnum.patrimony] as string,
          defaultValue: 100000,
          min: 0,
        }),
        [FieldEnum.annualInvestment]: getSafeDefaultValue({
          value: query[FieldEnum.annualInvestment] as string,
          defaultValue: 6000,
          min: 0
        }
        ),
        [FieldEnum.nbYears]: getSafeDefaultValue({
          value: query[FieldEnum.nbYears] as string, 
          defaultValue: 20, 
          min: 0, 
          max: 60
        }),
        [FieldEnum.stockYield]: getSafeDefaultValue({

          value: query[FieldEnum.stockYield] as string,
          defaultValue: 8,
          min: -100,
          max: 100
        }
        ),
        [FieldEnum.yieldOther]: getSafeDefaultValue({
          value: query[FieldEnum.yieldOther] as string,
          defaultValue: 5,
          min: -100,
          max: 100
        }),
        [FieldEnum.stockTax]: getSafeDefaultValue({

          value: query[FieldEnum.stockTax] as string,
          defaultValue: 17.2,
          min: 0,
          max: 100
        }
        ),
        [FieldEnum.tax]: getSafeDefaultValue({
          value: query[FieldEnum.tax] as string, 
          defaultValue: 30, 
          min: 0, 
          max: 100
        }),
        [FieldEnum.withdrawalRate]: getSafeDefaultValue({
          value: query[FieldEnum.withdrawalRate] as string,
          defaultValue: 4,
          min: 0,
          max: 100
        }
        ),
        [FieldEnum.inflationRate]: getSafeDefaultValue({
          value: query[FieldEnum.inflationRate] as string,
          defaultValue: 3,
          min: -100,
          max: 100
        }
         
        ),
        [FieldEnum.allowance]: getSafeDefaultValue({

          value: query[FieldEnum.allowance] as string,
          defaultValue: 50,
          min: 0,
          max: 100
        
        }),
        [FieldEnum.investAllowance]: getSafeDefaultValue({

          value: query[FieldEnum.investAllowance] as string,
          defaultValue: 50,
          min: 0,
          max: 100
        }),
      });
      initializedRef.current = true;
    } else if (!_isEqualWith(initialState, formValues) || paramsInitializedRef.current) {
      updateSimulatorQueryParams(formValues);
      paramsInitializedRef.current = true;
    }
  }, [isReady, query, formValues]);

  const onChangeField = (field, value) =>
    setFormValues((prev) => ({
      ...prev,
      [field]: value === '' || isNaN(Number(value)) ? '' : Number(value),
    }));

  return (
    <Container>
      <FormGrid>
        <Input
          name={FieldEnum.patrimony}
          label={formatMessage({ id: 'wealth_simulator.form.patrimony.label' })}
          placeholder={formatMessage({ id: 'wealth_simulator.form.patrimony.placeholer' })}
          tooltipInfos={formatMessage({ id: 'wealth_simulator.form.patrimony.tooltip' })}
          type="number"
          min={0}
          autoFocus={true}
          suffix={formatMessage({ id: 'wealth_simulator.form.patrimony.suffix' })}
          value={formValues[FieldEnum.patrimony]}
          onChange={(event) => onChangeField(FieldEnum.patrimony, event.target.value)}
        />
        <RangeInput
          name={FieldEnum.allowance}
          label={formatMessage({ id: 'wealth_simulator.form.allowance.label' })}
          displayPercentage
          minBottomLabel={formatMessage({
            id: 'wealth_simulator.form.allowance.minLabelBottom',
          })}
          maxBottomLabel={formatMessage({
            id: 'wealth_simulator.form.allowance.maxLabelBottom',
          })}
          value={formValues[FieldEnum.allowance]}
          onChange={(value) => onChangeField(FieldEnum.allowance, value)}
        />

        <Input
          name={FieldEnum.annualInvestment}
          label={formatMessage({ id: 'wealth_simulator.form.annual_investment.label' })}
          placeholder={formatMessage({
            id: 'wealth_simulator.form.annual_investment.placeholer',
          })}
          type="number"
          min={0}
          value={formValues[FieldEnum.annualInvestment]}
          suffix={formatMessage({ id: 'wealth_simulator.form.annual_investment.suffix' })}
          onChange={(event) => onChangeField(FieldEnum.annualInvestment, event.target.value)}
        />

        <RangeInput
          name={FieldEnum.investAllowance}
          label={formatMessage({ id: 'wealth_simulator.form.invest_allowance.label' })}
          displayPercentage
          value={formValues[FieldEnum.investAllowance]}
          minBottomLabel={formatMessage({
            id: 'wealth_simulator.form.invest_allowance.minLabelBottom',
          })}
          maxBottomLabel={formatMessage({
            id: 'wealth_simulator.form.invest_allowance.maxLabelBottom',
          })}
          onChange={(value) => onChangeField(FieldEnum.investAllowance, value)}
        />

        <Input
          name={FieldEnum.nbYears}
          label={formatMessage({ id: 'wealth_simulator.form.nb_years.label' })}
          placeholder={formatMessage({
            id: 'wealth_simulator.form.nb_years.placeholer',
          })}
          type="number"
          min={0}
          max={60}
          suffix={formatMessage({ id: 'wealth_simulator.form.nb_years.suffix' })}
          value={formValues[FieldEnum.nbYears]}
          onChange={(event) => onChangeField(FieldEnum.nbYears, event.target.value)}
        />
        <Input
          name={FieldEnum.stockYield}
          tooltipInfos={formatMessage({ id: 'wealth_simulator.form.stock_yield.tooltip' })}
          label={formatMessage({ id: 'wealth_simulator.form.stock_yield.label' })}
          placeholder={formatMessage({
            id: 'wealth_simulator.form.stock_yield.placeholer',
          })}
          type="number"
          min={-100}
          max={100}
          suffix={formatMessage({ id: 'wealth_simulator.form.stock_yield.suffix' })}
          value={formValues[FieldEnum.stockYield]}
          onChange={(event) => onChangeField(FieldEnum.stockYield, event.target.value)}
        />
        <Input
          name={FieldEnum.yieldOther}
          tooltipInfos={formatMessage({ id: 'wealth_simulator.form.yield.tooltip' })}
          label={formatMessage({ id: 'wealth_simulator.form.yield.label' })}
          placeholder={formatMessage({
            id: 'wealth_simulator.form.yield.placeholer',
          })}
          type="number"
          min={-100}
          max={100}
          suffix={formatMessage({ id: 'wealth_simulator.form.yield.suffix' })}
          value={formValues[FieldEnum.yieldOther]}
          onChange={(event) => onChangeField(FieldEnum.yieldOther, event.target.value)}
        />
        <DoubleGrid>
          <Input
            name={FieldEnum.stockTax}
            label={formatMessage({ id: 'wealth_simulator.form.stock_tax.label' })}
            tooltipInfos={formatMessage({ id: 'wealth_simulator.form.stock_tax.tooltip' })}
            placeholder={formatMessage({
              id: 'wealth_simulator.form.stock_tax.placeholer',
            })}
            type="number"
            min={0}
            max={100}
            suffix={formatMessage({ id: 'wealth_simulator.form.stock_tax.suffix' })}
            value={formValues[FieldEnum.stockTax]}
            onChange={(event) => onChangeField(FieldEnum.stockTax, event.target.value)}
          />
          <Input
            name={FieldEnum.tax}
            tooltipInfos={formatMessage({ id: 'wealth_simulator.form.tax.tooltip' })}
            label={formatMessage({ id: 'wealth_simulator.form.tax.label' })}
            placeholder={formatMessage({
              id: 'wealth_simulator.form.tax.placeholer',
            })}
            type="number"
            min={0}
            max={100}
            suffix={formatMessage({ id: 'wealth_simulator.form.tax.suffix' })}
            value={formValues[FieldEnum.tax]}
            onChange={(event) => onChangeField(FieldEnum.tax, event.target.value)}
          />
        </DoubleGrid>
        <Input
          tooltipInfos={formatMessage({ id: 'wealth_simulator.form.withdrawal_rate.tooltip' })}
          name={FieldEnum.withdrawalRate}
          label={formatMessage({ id: 'wealth_simulator.form.withdrawal_rate.label' })}
          placeholder={formatMessage({
            id: 'wealth_simulator.form.withdrawal_rate.placeholer',
          })}
          type="number"
          min={0}
          max={100}
          suffix={formatMessage({ id: 'wealth_simulator.form.withdrawal_rate.suffix' })}
          value={formValues[FieldEnum.withdrawalRate]}
          onChange={(event) => onChangeField(FieldEnum.withdrawalRate, event.target.value)}
        />
        <Input
          tooltipInfos={formatMessage({ id: 'wealth_simulator.form.inflation_rate.tooltip' })}
          name={FieldEnum.inflationRate}
          label={formatMessage({ id: 'wealth_simulator.form.inflation_rate.label' })}
          placeholder={formatMessage({
            id: 'wealth_simulator.form.inflation_rate.placeholer',
          })}
          type="number"
          min={-100}
          max={100}
          suffix={formatMessage({ id: 'wealth_simulator.form.inflation_rate.suffix' })}
          value={formValues[FieldEnum.inflationRate]}
          onChange={(event) => onChangeField(FieldEnum.inflationRate, event.target.value)}
        />
      </FormGrid>
      <GraphContainer>
        <Results values={formValues} />
        <SimulatorActionBlock utmCampaign="simulators_tools_wealthsimulator" />
      </GraphContainer>
    </Container>
  );
};

const Container = styled('div', {
  backgroundColor: 'Black',
  border: '1px solid #343434',
  margin: '80px 112px 0',
  borderRadius: '16px',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  '@sm': {
    margin: '32px 0 0',
    border: 'none',
    gridTemplateColumns: '1fr',
    borderRadius: '0',
  },
});

const FormGrid = styled('form', {
  gap: '48px',
  display: 'flex',
  flexDirection: 'column',
  padding: '48px',
  borderRight: '1px solid #343434',
  '@sm': {
    border: 'none',
    gap: '24px',
    padding: '40px 32px',
  },
});

const GraphContainer = styled('div', {
  overflow: 'auto',
});

const DoubleGrid = styled('div', {
  display: 'grid',
  gap: '32px',
  gridTemplateColumns: '1fr 1fr',
  '@sm': {
    gridTemplateColumns: '1fr',
    gap: '48px',
  },
});
